'use client';

import { toast } from '@/components/Toast';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager, {
  EAppCommunicationFuncName,
} from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { IS_US } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { isInZeloolApp } from '@/utils/tools';
import { useContext, useEffect, useState, type MouseEvent } from 'react';

interface TermsContentProps {
  html: string;
  className: string;
}

export default function TermsContent({ html, className }: TermsContentProps) {
  const token = useStore((state) => state.token);
  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const { deviceType } = useContext(ResponsiveContext);
  const isAppPlatform = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
  const [inAppH5, setInAppH5] = useState(isAppPlatform);
  const [appToken, setAppToken] = useState('');

  useEffect(() => {
    setInAppH5(isAppPlatform || isInZeloolApp());
  }, [isAppPlatform]);

  useEffect(() => {
    if (!IS_US || !inAppH5) return;

    let active = true;
    let callbackName = '';

    try {
      callbackName = appCommunicationManager.registerCallback(
        EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
        async (tokenFromApp: unknown) => {
          if (!active) return;

          const nextToken =
            typeof tokenFromApp === 'string' && tokenFromApp !== 'ClearToken' ? tokenFromApp : '';
          setAppToken(nextToken);

          if (nextToken) {
            await setToken(nextToken);
            await refreshAccountInfo();
          } else {
            await setToken('');
            setUserInfo({});
          }
        },
        {},
        'BackTokenInfo' as EAppCommunicationFuncName,
      );
    } catch (error) {
      logger.error('Failed to initialize terms page App auth:', error);
    }

    return () => {
      active = false;
      if (callbackName) appCommunicationManager.removeCallback(callbackName);
    };
  }, [inAppH5, refreshAccountInfo, setToken, setUserInfo]);

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    const isApp = inAppH5 || isInZeloolApp();
    const isLoggedIn = isApp ? Boolean(appToken) : Boolean(token);
    if (!IS_US || !isApp || isLoggedIn) return;

    const target = event.target;
    if (!(target instanceof Node)) return;

    const targetElement = target instanceof Element ? target : target.parentElement;
    const anchor = targetElement?.closest('a');
    const href = anchor?.getAttribute('href');
    if (
      !href ||
      new URL(href, window.location.origin).pathname !== '/dispute-resolution-application'
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    toast.info('Please log in first.');
  };

  return (
    <div
      className={className}
      onClickCapture={handleClick}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
